import {
  ExpandableSearch,
  IconButton,
  Selector,
  type SelectorOption,
} from "@somethings-ui/web";
import { PanelLeftClose, ShieldCheck, UsersRound } from "lucide-react";
import { useMemo, useState } from "react";
import { INTERNAL_OPS_CUSTOM_TYPES } from "@/lib/constants";
import { useChannelsList } from "@/lib/hooks/conversations/use-channels-list";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { cn } from "@/lib/utils";
import { useNoticeAnalytics } from "./notice-analytics";
import { useNoticeSidebar } from "./use-notice-sidebar";
import { getMentorWorkspaceLabel, isMentorWorkspace } from "./workspace";
import { useMentorWorkspace } from "./use-workspace";

interface MentorWorkspaceHeaderProps {
  onCollapse?: () => void;
  onSearchChange: (value: string) => void;
}

export function MentorWorkspaceHeader({
  onCollapse,
  onSearchChange,
}: MentorWorkspaceHeaderProps) {
  const { workspace, workspaces, setWorkspace } = useMentorWorkspace();
  const { searchQuery } = useNoticeSidebar();
  const { track } = useTrackEvent();
  const { trackWorkspaceSwitch, trackSearchOpen } = useNoticeAnalytics();
  const [searchExpanded, setSearchExpanded] = useState(false);

  const includeSupport = workspaces.some((option) => option.id === "support");
  const { channels: supportChannels } = useChannelsList({
    customTypesFilter: [...INTERNAL_OPS_CUSTOM_TYPES],
    enabled: includeSupport,
    enableNotifications: false,
  });
  const supportUnread = supportChannels.some(
    (channel) => channel.unreadMessageCount > 0,
  );

  const workspaceOptions = useMemo<SelectorOption[]>(
    () =>
      workspaces.map((option) => ({
        value: option.id,
        label: option.label,
        icon:
          option.id === "support" ? (
            <ShieldCheck className="size-[18px]" aria-hidden />
          ) : (
            <UsersRound className="size-[18px]" aria-hidden />
          ),
        unread: option.id === "support" && supportUnread,
      })),
    [supportUnread, workspaces],
  );

  const handleWorkspaceChange = (next: string) => {
    if (!isMentorWorkspace(next) || next === workspace) return;
    trackWorkspaceSwitch(workspace, next);
    if (workspace === "support" || next === "support") {
      track(PostHogEvents.OPS_CHAT_SEGMENT_TOGGLE, {
        from: workspace,
        to: next,
      });
    }
    // Route the reset through the search handler so it also cancels any pending
    // debounced search-use timer instead of firing for the abandoned query.
    onSearchChange("");
    setSearchExpanded(false);
    setWorkspace(next);
  };

  const handleSearchExpandedChange = (expanded: boolean) => {
    if (expanded && !searchExpanded) trackSearchOpen(workspace);
    setSearchExpanded(expanded);
  };

  const activeLabel = getMentorWorkspaceLabel(workspace);

  return (
    <header className="relative flex h-14 shrink-0 items-center border-b border-surfaces-z4 px-3">
      {workspaceOptions.length > 1 ? (
        <Selector
          options={workspaceOptions}
          value={workspace}
          onChange={handleWorkspaceChange}
          aria-label="Select workspace"
          triggerClassName={cn(
            "transition-opacity duration-150",
            searchExpanded && "pointer-events-none opacity-0",
          )}
        />
      ) : (
        <span
          className={cn(
            "truncate py-1 pl-2 font-heading text-header-4 text-text-primary transition-opacity duration-150",
            searchExpanded && "opacity-0",
          )}
        >
          {activeLabel}
        </span>
      )}

      <ExpandableSearch
        value={searchQuery}
        onChange={onSearchChange}
        expanded={searchExpanded}
        onExpandedChange={handleSearchExpandedChange}
        placeholder={`Search ${activeLabel.toLowerCase()}`}
        className={cn(
          "absolute top-1/2 -translate-y-1/2",
          onCollapse ? "right-14" : "right-3",
          searchExpanded
            ? onCollapse
              ? "w-[calc(100%-4.25rem)]"
              : "w-[calc(100%-1.5rem)]"
            : "w-10",
        )}
      />

      {onCollapse ? (
        <IconButton
          variant="tertiary"
          size="md"
          shape="circle"
          aria-label="Collapse sidebar"
          icon={<PanelLeftClose className="size-6 text-icon-secondary" />}
          onClick={onCollapse}
          className="absolute right-3 top-1/2 shrink-0 -translate-y-1/2"
        />
      ) : null}
    </header>
  );
}
